import React, { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import { getHamsProductKey } from '@atlassian/jira-growth-utils/src/services/get-hams-product-key/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { uiGetPaymentDetailsEnteredQuery } from '@atlassian/jira-relay/src/__generated__/uiGetPaymentDetailsEnteredQuery.graphql';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';

type PaidFeatureAwarenessRendererProps = {
	component: React.ReactNode;
	fallback?: React.ReactNode;
	placement: string;
};

type PaidFeatureAwarenessRendererInternalProps = {
	component: React.ReactNode;
	fallback: React.ReactNode | null;
	placement: string;
};

export const PaidFeatureAwarenessRendererInternal = (
	props: PaidFeatureAwarenessRendererInternalProps,
) => {
	const { component } = props;

	const cloudId = useCloudId();
	const hamsProductKey = getHamsProductKey(SOFTWARE);

	const data = useLazyLoadQuery<uiGetPaymentDetailsEnteredQuery>(
		graphql`
			query uiGetPaymentDetailsEnteredQuery($cloudId: ID!, $hamsProductKey: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: $hamsProductKey) {
						entitlement {
							transactionAccount {
								isBillToPresent
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			hamsProductKey,
		},
	);

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
	const hasPaymentDetails = entitlement?.transactionAccount?.isBillToPresent === true;

	if (hasPaymentDetails !== false) {
		return props.fallback;
	}

	return expValEquals(
		'persistent_paid_feature_awareness_ab',
		'cohort',
		'variation',
		'not-enrolled',
	) ? (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="paidFeatureAwareness"
			attributes={{ placement: props.placement }}
		>
			<FireScreenAnalytics />
			{component}
		</ContextualAnalyticsData>
	) : (
		props.fallback
	);
};

export const PaidFeatureAwarenessRendererComponent = (
	props: PaidFeatureAwarenessRendererInternalProps,
) => {
	const appEditions = useAppEditions();
	const jswEdition = appEditions.software;
	const productEntitlementDetails = useProductEntitlementDetails();

	const jswEntitlementDetails = productEntitlementDetails?.[SOFTWARE];
	const trialEndTime = jswEntitlementDetails?.trialEndTime;

	const currentDate = useMemo(() => Date.now(), []);
	const isOnJSWTrial = Boolean(trialEndTime && currentDate < trialEndTime);

	/** Eligibility criteria (in order of evaluation)
	 * - FG must be on
	 * - Must be on JSW Premium Trial
	 * - Must not have payment details entered (obtained via query)
	 * - Must be in variation for experiment (fire exposure at this point)
	 */
	const isOnJswPremiumTrial = jswEdition === PREMIUM_EDITION && isOnJSWTrial;
	return isOnJswPremiumTrial ? <PaidFeatureAwarenessRendererInternal {...props} /> : props.fallback;
};

export const PaidFeatureAwarenessRenderer = (props: PaidFeatureAwarenessRendererProps) => {
	const { fallback = null, ...rest } = props;

	return fg('persistent_paid_feature_awareness') ? (
		<JSErrorBoundary
			id="paidFeatureAwarenessBoundary"
			packageName="@atlassian/jira-paid-feature-awareness-renderer"
			fallback={fallback ? () => fallback : 'unmount'}
			teamName="growth-tako"
			sendToPrivacyUnsafeSplunk
			attributes={{ placement: props.placement }}
		>
			<Placeholder name="paid-feature-awareness-renderer-placeholder" fallback={null}>
				<PaidFeatureAwarenessRendererComponent fallback={fallback} {...rest} />
			</Placeholder>
		</JSErrorBoundary>
	) : (
		fallback
	);
};
